import { __assign, __extends, __spreadArrays } from "tslib";
import * as React from 'react';
import { VerticalTabs, Tab, Divider } from '@wix/wix-base-ui';
import { SettingsTabLayoutContext } from './SettingsTabLayoutContext';
var counter = 0;
function getTabId() {
    return "tab-" + ++counter;
}
function navigateToBilling(SDKAdapter, tab) {
    if (SDKAdapter) {
        SDKAdapter.navigateToBilling({
            referrer: tab.props.referrer,
            premiumIntent: tab.props.premiumIntent,
        });
    }
    else {
        console.warn('SettingsTabLayout: can not navigate to billing. – `SDK` prop is required');
    }
}
var BaseSettingsTabLayout = /** @class */ (function (_super) {
    __extends(BaseSettingsTabLayout, _super);
    function BaseSettingsTabLayout() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.state = {
            tabs: [],
            currentTabId: null,
        };
        _this.handleAddTab = function (tab) {
            var tabId = getTabId();
            if (tab.type === 0 /* Tab */ &&
                !tab.props.Component &&
                !tab.props.children) {
                console.error('[tpa-settings] `<SettingsTabLayout.Tab>` should contain Component or Children prop');
            }
            if (tab.type === 0 /* Tab */ || tab.type === 2 /* TabUpgrade */) {
                if (tab.props.id) {
                    tabId = tab.props.id;
                }
                if (_this.state.tabs.findIndex(function (t) { return t.id === tabId; }) !== -1) {
                    throw new Error("Tab with id \"" + tabId + "\" already exists");
                }
            }
            _this.setState(function (state) {
                var tabs = __spreadArrays(state.tabs, [
                    __assign(__assign({}, tab), { id: tabId }),
                ]);
                return __assign(__assign({}, state), { currentTabId: state.currentTabId || tabs[0].id, tabs: tabs });
            });
            return {
                removeTab: function () {
                    _this.setState(function (state) {
                        var index = state.tabs.findIndex(function (t) { return t.id === tabId; });
                        if (index !== -1) {
                            return {
                                tabs: _this.state.tabs.slice(index, 1),
                            };
                        }
                    });
                },
                updateTab: function (props) {
                    _this.setState(function (state) {
                        var index = state.tabs.findIndex(function (t) { return t.id === tabId; });
                        if (index !== -1) {
                            var tabs = __spreadArrays(state.tabs);
                            tabs[index] = __assign(__assign({}, props), { id: tabs[index].id });
                            return {
                                tabs: tabs,
                            };
                        }
                    });
                },
            };
        };
        _this.handleTabChange = function (tabId) {
            var selectedTab = _this.state.tabs.find(function (tab) { return tab.id === tabId; });
            if (selectedTab.type === 2 /* TabUpgrade */) {
                navigateToBilling(_this.props.SDKAdapter, selectedTab);
                return;
            }
            if (selectedTab.type === 3 /* Button */) {
                selectedTab.props.onClick();
                return;
            }
            _this.setActiveTab(tabId);
        };
        return _this;
    }
    BaseSettingsTabLayout.prototype.componentDidMount = function () {
        if (this.props.initialTabId) {
            this.setState({ currentTabId: this.props.initialTabId });
        }
        if (!this.state.tabs.length) {
            return;
        }
    };
    BaseSettingsTabLayout.prototype.componentDidUpdate = function (props, state) {
        if (!props.tabId && this.props.tabId) {
            console.warn('SettingsTabLayout – should not switch from uncontrolled to controlled (or vice versa).');
            return;
        }
        // Component is controlled
        if (this.props.tabId) {
            // New tab was added, which is marked as selected via `tabId` prop.
            if (this.state.tabs.length > state.tabs.length) {
                var addedTab = this.state.tabs[this.state.tabs.length - 1];
                if (addedTab.type === 0 /* Tab */ &&
                    this.props.tabId === addedTab.props.id) {
                    this.onActiveTabChanged(addedTab.props.id);
                    return;
                }
            }
            // `tabId` prop was changed.
            if (props.tabId !== this.props.tabId) {
                this.onActiveTabChanged(this.props.tabId);
                return;
            }
        }
        if (state.currentTabId !== this.state.currentTabId) {
            this.onActiveTabChanged(this.state.currentTabId);
        }
    };
    BaseSettingsTabLayout.prototype.onActiveTabChanged = function (tabId) {
        var _a, _b;
        if (this.props.SDKAdapter) {
            var currentTab = this.state.tabs.find(function (t) { return t.id === tabId; });
            if (currentTab &&
                currentTab.type === 0 /* Tab */ &&
                currentTab.props.articleId) {
                (_a = this.props.bi) === null || _a === void 0 ? void 0 : _a.settingsTabClicked({
                    tabName: (_b = currentTab.props.id) !== null && _b !== void 0 ? _b : currentTab.props.title,
                });
                this.props.SDKAdapter.setHelpArticle(currentTab.props.articleId);
            }
        }
        else {
            console.warn('SettingsTabLayout – not possible to track the tab change – `SDK` prop is required');
        }
    };
    BaseSettingsTabLayout.prototype.setActiveTab = function (tabId) {
        // if uncontrolled component
        if (!this.props.tabId) {
            this.setState({
                currentTabId: tabId,
            });
        }
        if (this.props.onChange) {
            this.props.onChange(tabId);
        }
    };
    BaseSettingsTabLayout.prototype.render = function () {
        var _this = this;
        var currentTabId;
        /**
         * NOTE: may be issues with tabIds like `0`
         */
        if (this.props.tabId) {
            currentTabId = this.props.tabId;
        }
        else if (this.state.currentTabId && this.state.tabs.length > 0) {
            currentTabId = this.state.tabs.find(function (tab) { return tab.id === _this.state.currentTabId; })
                ? this.state.currentTabId
                : this.state.tabs[0].id;
        }
        else if (this.state.tabs.length > 0) {
            currentTabId = this.state.tabs[0].id;
        }
        var currentTab = currentTabId
            ? this.state.tabs.find(function (tab) { return tab.id === currentTabId; })
            : null;
        var _a = this.props, _b = _a.dataHook, dataHook = _b === void 0 ? 'settings-tab-layout' : _b, _c = _a.tabContentDataHook, tabContentDataHook = _c === void 0 ? 'settings-tab-layout' : _c;
        return (React.createElement(SettingsTabLayoutContext.Provider, { value: {
                addTab: this.handleAddTab,
                currentTabId: currentTabId,
            } },
            this.props.children,
            React.createElement("div", { "data-hook": dataHook, style: {
                    height: '100%',
                    width: '100%',
                    display: 'flex',
                } },
                React.createElement("div", { style: {
                        maxWidth: '114px',
                    } }, currentTabId ? (React.createElement(VerticalTabs, { value: currentTabId, onChange: this.handleTabChange }, this.state.tabs.map(function (tab, index) {
                    var value = tab.id || index;
                    switch (tab.type) {
                        case 1 /* Divider */: {
                            return React.createElement(Divider, { key: "settings-tab-divider-" + index });
                        }
                        case 2 /* TabUpgrade */: {
                            return (React.createElement(Tab, { key: value, value: value, label: tab.props.title, automationId: "settings-tab-" + value, dataHook: "settings-tab-" + value, className: "upgrade" }));
                        }
                        case 0 /* Tab */: {
                            return (React.createElement(Tab, { showDotIndicator: tab.props.showDotIndicator || false, key: value, value: value, label: tab.props.title, dataHook: tab.props.dataHook
                                    ? tab.props.dataHook
                                    : "settings-tab-" + value, automationId: tab.props.automationId
                                    ? tab.props.automationId
                                    : "settings-tab-" + value, className: tab.props.className }));
                        }
                        case 3 /* Button */:
                            return (React.createElement(Tab, { key: value, value: value, label: tab.props.title, automationId: "settings-tab-" + value, dataHook: "settings-tab-" + value, className: tab.props.className }, tab.props.title));
                        default:
                            return null;
                    }
                }))) : null),
                React.createElement("div", { "data-hook": tabContentDataHook, style: {
                        flex: 1,
                        overflow: 'auto',
                        maxWidth: '290px',
                    } }, currentTab && currentTab.type === 0 /* Tab */ ? (typeof currentTab.props.Component === 'function' ? (React.createElement(currentTab.props.Component, null)) : currentTab.props.children ? (currentTab.props.children()) : null) : null))));
    };
    return BaseSettingsTabLayout;
}(React.Component));
export { BaseSettingsTabLayout };
//# sourceMappingURL=BaseSettingsTabLayout.js.map